import {
  TrendingUp,
  Clock,
  ShieldCheck,
  Stethoscope,
  ArrowRight,
  Check,
  Activity,
  Calendar,
  FileText,
  Users,
} from "lucide-react";

const CASES = [
  {
    id: "mediflow",
    badge: "Caso de Uso: Sector Salud",
    badgeIcon: Stethoscope,
    title: "Sistema Integral MediFlow",
    description:
      "Optimización de una clínica médica mediante automatización de agendas y expedientes digitales. Reducción de tiempos muertos, expedientes consultables desde cualquier dispositivo y reportes inteligentes con IA.",
    metrics: [
      {
        icon: TrendingUp,
        value: "+25%",
        label: "Incremento en ingresos",
        accent: "orange",
      },
      {
        icon: Clock,
        value: "10 hrs/semana",
        label: "Ahorradas por médico",
        accent: "sky",
      },
      {
        icon: ShieldCheck,
        value: "100%",
        label: "Seguridad en expedientes",
        accent: "orange",
      },
    ],
    tags: ["React", "FastAPI", "MongoDB", "IA", "HL7"],
  },
];

import { Rich } from "@/components/Rich";
import { withDefaults } from "@/lib/sections";

export default function CaseStudies({ content }) {
  const c = withDefaults("cases", content);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="casos"
      data-testid="section-cases"
      className="relative py-20 sm:py-28 border-t border-slate-900"
    >
      <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl">
          <div className="font-mono text-xs text-sky-400 mb-3">
            {c.eyebrow}
          </div>
          <h2
            data-testid="cases-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
          >
            <Rich text={c.title} />
          </h2>
          <p className="mt-4 text-slate-400 text-lg leading-relaxed">
            {c.subtitle}
          </p>
        </div>

        {/* Cases grid */}
        <div className="mt-12 grid grid-cols-1 gap-8">
          {CASES.map((c) => (
            <CaseCard key={c.id} caseData={c} />
          ))}
        </div>

        {/* CTA below */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-800 bg-slate-900/40 p-6">
          <p className="text-slate-400 text-sm">
            <span className="text-white font-medium">¿Quieres ser nuestro próximo caso de éxito?</span>{" "}
            Agenda una llamada y mapeamos tu proceso en 15 minutos.
          </p>
          <button
            onClick={() => goTo("agendar")}
            data-testid="cases-cta-book"
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm text-white bg-orange-500 hover:bg-orange-600 transition-all shadow-[0_0_18px_rgba(249,115,22,0.3)] hover:shadow-[0_0_28px_rgba(249,115,22,0.5)]"
          >
            Empezar mi proyecto
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}

function CaseCard({ caseData }) {
  const BadgeIcon = caseData.badgeIcon;
  return (
    <article
      data-testid={`case-card-${caseData.id}`}
      className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 shadow-lg shadow-slate-950/50 transition-all duration-300 hover:-translate-y-1 hover:border-sky-500/40 hover:shadow-[0_25px_60px_-15px_rgba(14,165,233,0.25)]"
    >
      {/* Subtle gradient overlay on hover */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-sky-500/0 via-transparent to-orange-500/0 group-hover:from-sky-500/[0.04] group-hover:to-orange-500/[0.06] transition-all duration-500 pointer-events-none"
        aria-hidden
      />

      <div className="relative grid lg:grid-cols-5 gap-0">
        {/* Left: content */}
        <div className="lg:col-span-3 p-7 sm:p-10">
          <span
            data-testid={`case-badge-${caseData.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-medium bg-sky-500/10 text-sky-300 border border-sky-500/30"
          >
            <BadgeIcon size={13} /> {caseData.badge}
          </span>

          <h3 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            {caseData.title}
          </h3>
          <p className="mt-3 text-slate-400 leading-relaxed text-sm sm:text-base">
            {caseData.description}
          </p>

          {/* Metrics */}
          <div className="mt-7 grid sm:grid-cols-3 gap-4">
            {caseData.metrics.map((m, i) => {
              const Icon = m.icon;
              const isOrange = m.accent === "orange";
              return (
                <div
                  key={i}
                  data-testid={`case-metric-${caseData.id}-${i}`}
                  className="relative rounded-xl border border-slate-800 bg-slate-950/50 p-4"
                >
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${
                      isOrange
                        ? "bg-orange-500/10 text-orange-400 border border-orange-500/30"
                        : "bg-sky-500/10 text-sky-400 border border-sky-500/30"
                    }`}
                  >
                    <Icon size={18} />
                  </span>
                  <div className="mt-3 text-xl sm:text-2xl font-bold text-white font-mono">
                    {m.value}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{m.label}</div>
                </div>
              );
            })}
          </div>

          {/* Tech tags */}
          <div className="mt-6 flex flex-wrap gap-1.5">
            {caseData.tags.map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 text-[10px] font-mono rounded bg-slate-800 text-slate-400 border border-slate-700"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Right: dashboard mockup */}
        <div className="lg:col-span-2 relative bg-slate-950/60 border-t lg:border-t-0 lg:border-l border-slate-800 p-6 sm:p-7 flex items-center justify-center">
          <MediFlowMockup />
        </div>
      </div>
    </article>
  );
}

function MediFlowMockup() {
  return (
    <div
      data-testid="case-mockup-mediflow"
      className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
    >
      {/* Window header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-950/60">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
        </div>
        <span className="font-mono text-[10px] text-slate-500">
          mediflow.app
        </span>
      </div>

      {/* Mini sidebar + content */}
      <div className="flex">
        <div className="w-10 border-r border-slate-800 bg-slate-950/40 py-3 flex flex-col items-center gap-3">
          <Activity size={14} className="text-orange-400" />
          <Users size={14} className="text-slate-600" />
          <Calendar size={14} className="text-slate-600" />
          <FileText size={14} className="text-slate-600" />
        </div>

        <div className="flex-1 p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-500">// dashboard</div>
              <div className="text-xs font-semibold text-white mt-0.5">Hoy</div>
            </div>
            <div className="font-mono text-[10px] text-sky-400 px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/30">
              live
            </div>
          </div>

          {/* Stat row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-slate-800 bg-slate-950/60 p-2">
              <div className="text-[9px] text-slate-500">Citas</div>
              <div className="text-sm font-bold text-white font-mono">24</div>
            </div>
            <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-2">
              <div className="text-[9px] text-orange-400">Ingresos</div>
              <div className="text-sm font-bold text-white font-mono">$48k</div>
            </div>
          </div>

          {/* Bar chart */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[9px] font-mono text-slate-500">// citas.semana</div>
              <div className="text-[9px] text-emerald-400">+25%</div>
            </div>
            <div className="flex items-end gap-1 h-12">
              {[40, 65, 50, 80, 55, 90, 75].map((h, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-sm ${
                    i === 5 ? "bg-orange-500" : "bg-sky-500/60"
                  }`}
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
            <div className="flex gap-1 mt-1 text-[8px] text-slate-600 font-mono">
              {["L", "M", "M", "J", "V", "S", "D"].map((d) => (
                <div key={d} className="flex-1 text-center">
                  {d}
                </div>
              ))}
            </div>
          </div>

          {/* Task list */}
          <div>
            <div className="text-[9px] font-mono text-slate-500 mb-1.5">
              // pendientes
            </div>
            <ul className="space-y-1.5">
              <TaskItem text="Receta · Pérez" done />
              <TaskItem text="Confirmar cita · 14:30" done />
              <TaskItem text="Resultados · López" />
            </ul>
          </div>

          {/* Progress */}
          <div>
            <div className="flex justify-between text-[9px] mb-1">
              <span className="text-slate-500 font-mono">// expedientes</span>
              <span className="text-white">87%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-sky-500 to-orange-500"
                style={{ width: "87%" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskItem({ text, done }) {
  return (
    <li className="flex items-center gap-2 text-[10px]">
      <span
        className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${
          done
            ? "bg-emerald-500/20 border-emerald-500/40"
            : "border-slate-700 bg-slate-900"
        }`}
      >
        {done && <Check size={9} className="text-emerald-400" />}
      </span>
      <span className={done ? "text-slate-500 line-through" : "text-slate-200"}>
        {text}
      </span>
    </li>
  );
}
